export interface SupportMessage {
  id: string;
  content: string;
  sender: "user" | "agent" | "bot";
  timestamp: string;
  tempId?: string;
  authorId?: string;
  isOptimistic?: boolean;
  deliveryStatus?: "sending" | "sent" | "failed";
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

export const isSupportMessage = (value: unknown): value is SupportMessage => {
  const message = asRecord(value);
  return Boolean(message && nonEmptyString(message.id) &&
    typeof message.content === "string" && message.content.trim() &&
    (message.sender === "user" || message.sender === "agent" || message.sender === "bot") &&
    typeof message.timestamp === "string" && Number.isFinite(Date.parse(message.timestamp)));
};

export const normalizeMessage = (
  raw: unknown,
  senderId: string
): SupportMessage | null => {
  const message = asRecord(raw);
  if (!message) return null;

  const id = nonEmptyString(message._id) ?? nonEmptyString(message.id);
  const content = message.content;
  const rawTimestamp = message.createdAt ?? message.timestamp;
  if (
    !id ||
    typeof content !== "string" ||
    !content.trim() ||
    !(
      (typeof rawTimestamp === "string" && rawTimestamp.trim()) ||
      (typeof rawTimestamp === "number" && Number.isFinite(rawTimestamp))
    )
  ) {
    return null;
  }

  const timestamp = new Date(rawTimestamp as string | number);
  if (!Number.isFinite(timestamp.getTime())) return null;

  const sender = asRecord(message.sender);
  const authorId = nonEmptyString(sender?._id) ?? nonEmptyString(message.sender);
  const tempId = nonEmptyString(message.tempId);

  return {
    id,
    content,
    sender:
      authorId === "bot" || message.isBot === true
        ? "bot"
        : authorId === senderId
          ? "user"
          : "agent",
    timestamp: timestamp.toISOString(),
    ...(authorId ? { authorId } : {}),
    ...(tempId ? { tempId } : {}),
    isOptimistic: false,
    deliveryStatus: "sent",
  };
};

export const readMessages = (
  payload: unknown,
  senderId: string
): SupportMessage[] => {
  const record = asRecord(payload);
  const messages = Array.isArray(payload) ? payload : record?.messages;
  if (!Array.isArray(messages)) return [];
  return messages.flatMap((raw) => {
    const message = normalizeMessage(raw, senderId);
    return message ? [message] : [];
  });
};

export const readSentMessage = (
  payload: unknown,
  senderId: string
): SupportMessage | null => {
  const record = asRecord(payload);
  if (!record) return null;
  const data = asRecord(record.data);
  for (const candidate of [record, record.message, data, data?.message]) {
    const message = normalizeMessage(candidate, senderId);
    if (message) return message;
  }
  return null;
};

const timestampValue = (message: SupportMessage): number => {
  const value = Date.parse(message.timestamp);
  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
};

const combineMessage = (
  existing: SupportMessage,
  incoming: SupportMessage
): SupportMessage => {
  // A late local request completion must not turn a confirmed echo back into a
  // pending or failed message.
  if (!existing.isOptimistic && incoming.isOptimistic) return existing;
  return {
    ...existing,
    ...incoming,
    ...(!incoming.tempId && existing.tempId ? { tempId: existing.tempId } : {}),
  };
};

export const mergeMessages = (
  current: SupportMessage[],
  incoming: SupportMessage[]
): SupportMessage[] => {
  const merged = current.map((message) => ({ ...message }));

  for (const message of incoming) {
    let match = merged.findIndex((existing) => existing.id === message.id);

    if (match === -1 && message.tempId) {
      match = merged.findIndex(
        (existing) =>
          existing.tempId === message.tempId &&
          // Two confirmed server IDs always represent distinct messages.
          (existing.isOptimistic || message.isOptimistic)
      );
    }

    if (match === -1 && !message.isOptimistic && message.sender === "user") {
      // Older backends omit tempId. Only reconcile an unambiguous local send;
      // never use text similarity to deduplicate confirmed history.
      const candidates = merged.flatMap((existing, index) => {
        const timeDifference =
          timestampValue(message) - timestampValue(existing);
        return existing.isOptimistic &&
          existing.sender === "user" &&
          existing.content === message.content &&
          timeDifference >= -2_000 &&
          timeDifference <= 60_000
          ? [index]
          : [];
      });
      if (candidates.length === 1) match = candidates[0];
    }

    if (match === -1) {
      merged.push({ ...message });
    } else {
      merged[match] = combineMessage(merged[match], message);
    }
  }

  // Array.sort is stable: messages with equal timestamps keep their existing
  // order, including live messages that arrived while history was loading.
  return merged.sort((left, right) => timestampValue(left) - timestampValue(right));
};
